# Flows & Decisions

Understanding Taias's internal model helps you use it effectively and anticipate how it will evolve.

---

## The Core Abstraction

Taias is a **decision engine**. It takes context about the current tool call and produces a **Decision** - a generalized object representing what should happen next. That decision is then **manifested** into concrete outputs:

```
Inputs -> Decision -> Manifestations
```

| Stage              | What It Does                    | Example                                                                           |
| ------------------ | ------------------------------- | --------------------------------------------------------------------------------- |
| **Inputs**         | Context about the current state | `{ toolName: "get_recent_tracks", params: { limit: 20 }, result: { count: 15 } }` |
| **Decision**       | What should happen next         | `{ nextTool: "get_recommendations" }`                                             |
| **Manifestations** | How the decision appears        | LLM advice, UI selections                                                         |

---

## Separation of Concerns

This architecture separates two distinct responsibilities:

**Flow logic** determines _what should happen next_.

- Defined via `defineFlow()` as logic statements
- Logic statements express match conditions and decisions as structured data
- Produces a `StepDecision` -- an object with one or more decision fields
- Pure business/product logic

**Manifestation layers** determine _how that decision appears_.

- LLM advice: Auto-generated text guidance
- UI selections: Which handles to activate for each slot
- (Future) Additional output types

This separation means:

- You can change your flow logic without touching UI code
- You can add new manifestation types without changing flows
- The decision is a stable intermediate representation

---

## Logic Statements

Each step in a flow is a **logic statement** -- structured data that Taias can understand, validate, and optimize. A logic statement pairs a match condition with a decision:

```ts
flow.step(
  { toolName: { is: "get_recent_tracks" } },
  { nextTool: "get_recommendations" },
);
```

The match condition determines when the statement applies, and the decision object is returned directly with no function invocation. Match conditions use explicit operators (`{ is: ... }`, `{ isNot: ... }`) and can match on `toolName`, `params`, and `result` -- see [Match Conditions](/docs#flows-and-decisions/match-conditions) below.

Logic statements can include multiple decision fields:

```ts
flow.step(
  { toolName: { is: "get_recent_tracks" } },
  {
    nextTool: "get_recommendations",
    variant: "discovery",
  },
);
```

> **Backwards compatibility:** Handler functions (which receive `TaiasContext` and return a decision dynamically) are also supported but are not the recommended approach. Logic statements are the core primitive of the decision engine.

---

## Match Conditions

`flow.step()` takes two arguments: a **match condition** object (the conditions under which this step applies) and a **decision** object (what to produce when matched):

```ts
flow.step(
  { toolName: { is: "get_recent_tracks" } },
  { nextTool: "get_recommendations" },
);
```

All fields in a `MatchCondition` are optional. Steps can match on any combination of `toolName`, `params`, and `result`:

```ts
// Match on toolName only
flow.step({ toolName: { is: "scan_repo" } }, { nextTool: "configure_app" });

// Match on toolName and params
flow.step(
  { toolName: { is: "scan_repo" }, params: { language: { is: "python" } } },
  { nextTool: "configure_python" },
);

// Match on result only (applies regardless of toolName)
flow.step(
  { result: { hasConfig: { is: true } } },
  { nextTool: "review_config" },
);

// Match on toolName, params, and result
flow.step(
  {
    toolName: { is: "scan_repo" },
    params: { language: { is: "python" } },
    result: { hasConfig: { is: true } },
  },
  { nextTool: "review_python_config" },
);
```

### Operators

Each field in a match condition uses an **operator object** that specifies how to compare the value. Two operators are currently supported:

**`is`** -- exact equality (strict `===`). The step matches when the field equals the given value:

```ts
flow.step(
  { toolName: { is: "get_recent_tracks" } },
  { nextTool: "get_recommendations" },
);
```

**`isNot`** -- negation (strict `!==`). The step matches when the field does _not_ equal the given value:

```ts
flow.step(
  { toolName: { isNot: "abort_session" } },
  { nextTool: "continue_flow" },
);
```

Operators accept values of any type (`unknown`), enabling matching on strings, numbers, booleans, and more:

```ts
flow.step(
  { result: { hasConfig: { is: true } } },
  { nextTool: "review_config" },
);
flow.step(
  { result: { fileCount: { is: 42 } } },
  { nextTool: "exact_count_handler" },
);
```

Operators are pure data -- they are objects that Taias interprets, not wrapper functions. This aligns with the logic-as-data philosophy: Taias can inspect, validate, and optimize operator conditions at build time.

### Subset Matching for params and result

For `params` and `result`, only the specified keys are checked against the context. Unspecified keys in the context are ignored:

```ts
flow.step(
  { params: { language: { is: "python" } } },
  { nextTool: "python_handler" },
);

// Matches: { toolName: "scan_repo", params: { language: "python", repoUrl: "..." } }
// (repoUrl is ignored -- only language is checked)
```

If a step specifies `params` or `result` conditions but the context doesn't include `params` or `result`, the step does not match.

### Match Priority

When multiple steps could match the same context, Taias evaluates indexed steps (those with `is` conditions) before broad steps (those with only `isNot` conditions). Within each group, definition order applies (first match wins). This means a step with an `is`-based match will be found before a pure `isNot`-based step.

```ts
const flow = defineFlow("example", (flow) => {
  // This step matches scan_repo specifically (indexed)
  flow.step({ toolName: { is: "scan_repo" } }, { nextTool: "configure_app" });

  // This step matches anything except abort_session (broad)
  flow.step(
    { toolName: { isNot: "abort_session" } },
    { nextTool: "continue_flow" },
  );
});

// resolve({ toolName: "scan_repo" })     -> "configure_app"  (indexed match)
// resolve({ toolName: "create_user" })   -> "continue_flow"  (broad match)
// resolve({ toolName: "abort_session" }) -> null              (no match)
```

The `MatchCondition` type and operator system are designed to expand with additional fields and operators as the decision engine evolves.

---

## The Decision Object

The Decision is an object with one or more fields, produced by evaluating a logic statement against the context provided.

```ts
type Decision = Record<string, string | undefined>;
```

The decision object describes what should be projected to the LLM and the user. The object is then referenced by the manifestation layer to put forth the corresponding LLM advice and UI variants.

---

## Why This Matters

When you call `resolve()`, you're not just getting "advice text" - you're getting a complete decision package:

```ts
const result = await taias.resolve({ toolName: "get_recent_tracks" });

// result.decision   -> The raw decision ({ nextTool: "get_recommendations" })
// result.advice     -> LLM manifestation of that decision
// result.selections -> UI manifestation of that decision
```

Your tools can use whichever manifestations are relevant:

- MCP servers without UI might only use `advice`
- Rich widget experiences might primarily use `selections`
- Advanced integrations might inspect `decision` directly

---

## Extensibility

The `Inputs -> Decision -> Manifestations` pipeline is designed for extension:

**Operators**: Match condition fields support comparison operators (`is`, `isNot`). Future versions will add more operators (e.g., `oneOf`, `contains`) as the decision engine evolves.

**Match conditions**: `MatchCondition` supports `toolName`, `params`, and `result`, with all fields optional. Steps can match on any combination of these fields. Future versions will expand with additional context fields (e.g., state tuples) as the decision engine evolves. All fields use the same operator system.

**Conditional logic**: As the decision engine matures, logic statements will gain the ability to express conditional branches declaratively -- e.g., "given this tool and these params, if the result indicates X, then decide Y; otherwise decide Z."

**Multiple decision fields**: Logic statements can include any number of custom fields. The decision object is not limited to a single field -- it can describe actions, data preferences, theme settings, business considerations, and more. Future versions may introduce common field conventions for these categories.

**Custom slots**: Define your own slot types with full TypeScript type safety. See [Advanced Patterns](/docs#advanced-patterns) for details.

**More manifestation types** (future): Beyond advice and UI selections, Taias could generate analytics events, notification triggers, or other outputs from the same decision.
